<script lang="ts">
  /**
   * Sankey of the Bousfield-DAG — the FLOW / distribution dimension.
   *
   * FLOW (default): a fractal flow of the shared process-commons tree, rooted at
   *   the current scope (the whole commons, or the selected track). Click a node
   *   to reveal its depth — children nest inline as the next column (expand-in-
   *   place, no breadcrumb). Click a flow (link) to follow/expand it. Link width
   *   = subtree weight, so a heavy collapsed branch visibly invites a click. This
   *   IS the "Google-Maps-zoom over Sankey data" — keep clicking to go deeper,
   *   to the bottom of the DAG→HTN→DAG (skills/processes/audits).
   * PIVOT: an analytic re-grouping of the fact table by any scope variables
   *   (layer chips) with per-facet filters. Portfolio-wide.
   */
  import { onMount } from 'svelte'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  import { sankey, sankeyLinkHorizontal, sankeyLeft } from 'd3-sankey'
  import { fetchTaskSankey, type SankeyRow, type TaskMilestone } from './api.svelte'
  import {
    selectMilestone, selectConnection, loadTree, treeState,
    visibleSubtree, toggleExpand,
  } from './taskGraphStore.svelte'

  type TrackMin = { name: string; refs: number[]; way_id?: number }
  type ModelMin = { M: Map<number, { state: string | null; title: string | null }>; bySlug: Map<string, TrackMin> }
  let { slug = null, model = null }: { slug?: string | null; model?: ModelMin | null } = $props()

  const ALL_DIMS = ['namespace', 'owner', 'visibility', 'kind', 'graph', 'lifecycle', 'way', 'gate', 'state'] as const
  type Dim = (typeof ALL_DIMS)[number]
  const FILTER_FACETS: Dim[] = ['visibility', 'kind', 'lifecycle', 'gate', 'state']

  const STATE_COLOR: Record<string, string> = {
    done: '#3fb950', active: '#58a6ff', next: '#d29922', waiting: '#db6d28',
    planned: '#5b6472', reference: '#8957e5', step: '#5b6472', milestone: '#5b6472', other: '#484f58',
  }
  const VIS_COLOR: Record<string, string> = { public: '#3fb950', marketplace: '#d29922', org: '#58a6ff', private: '#5b6472' }
  const DIM_COLOR: Record<Dim, string> = {
    namespace: '#39d0d8', owner: '#2aa9c0', visibility: '#3b82c4', kind: '#8957e5',
    graph: '#2f9bd8', lifecycle: '#db6d28', way: '#5a6bd8', gate: '#bb8009', state: '#5b6472',
  }
  const KIND_COLOR: Record<string, string> = { namespace: '#39d0d8', graph: '#2f9bd8', way: '#5a6bd8', node: '#7d8590', root: '#39d0d8' }
  const W = 920

  let rows = $state<SankeyRow[]>([])
  let loading = $state(true)
  let error = $state<string | null>(null)
  let pmode = $state<'flow' | 'pivot'>('flow')
  let layers = $state<Dim[]>(['namespace', 'graph', 'state'])
  let filters = $state<Record<string, string[]>>({})
  let showFilters = $state(false)

  onMount(async () => {
    try { const [s] = await Promise.all([fetchTaskSankey(), loadTree()]); rows = s.rows }
    catch (e) { error = (e as Error).message }
    finally { loading = false }
  })

  function nodeColor(kind: string, state: string | null): string {
    if (kind === 'node') return STATE_COLOR[state ?? 'other'] || STATE_COLOR.other
    return KIND_COLOR[kind] || '#7d8590'
  }

  // ── FLOW (fractal, expand-in-place) ──
  const rootId = $derived(slug && model ? ('w:' + (model.bySlug.get(slug)?.way_id ?? '')) : 'all')
  const rootLabel = $derived(treeState.byId.get(rootId)?.label ?? (rootId === 'all' ? 'process commons' : (slug ?? rootId)))

  // leaf-count over the FULL tree → link width reflects a branch's total weight
  const leafCount = $derived.by(() => {
    const co = treeState.childrenOf
    const memo = new Map<string, number>()
    const f = (id: string): number => {
      const c = memo.get(id); if (c != null) return c
      const ks = co.get(id) ?? []
      const v = ks.length ? ks.reduce((a, k) => a + f(k.id), 0) : 1
      memo.set(id, v); return v
    }
    return f
  })

  const flow = $derived.by(() => {
    if (pmode !== 'flow' || !treeState.loaded) return null
    const vis = visibleSubtree(rootId)
    if (!vis.length) return null
    const lc = leafCount
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nodes: any[] = []
    const idx = new Map<string, number>()
    const root = treeState.byId.get(rootId)
    idx.set(rootId, nodes.length)
    nodes.push({ tid: rootId, name: rootLabel, kind: root?.kind ?? 'root', hasKids: true, open: true, color: nodeColor(root?.kind ?? 'root', null) })
    const counts = new Map<number, number>([[0, 1]])
    for (const v of vis) {
      idx.set(v.rec.id, nodes.length)
      nodes.push({ tid: v.rec.id, name: v.rec.label, kind: v.rec.kind, state: v.rec.state ?? null, hasKids: v.hasKids, open: v.open, color: nodeColor(v.rec.kind, v.rec.state ?? null) })
      counts.set(v.depth + 1, (counts.get(v.depth + 1) ?? 0) + 1)
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const links: any[] = []
    for (const v of vis) {
      const p = v.rec.parent
      if (p == null || !idx.has(p) || !idx.has(v.rec.id)) continue
      links.push({ source: idx.get(p)!, target: idx.get(v.rec.id)!, value: Math.max(1, lc(v.rec.id)), tcolor: nodeColor(v.rec.kind, v.rec.state ?? null) })
    }
    const H = Math.max(360, Math.max(...counts.values()) * 20)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sk = (sankey() as any).nodeWidth(13).nodePadding(8).nodeAlign(sankeyLeft).nodeSort(null).extent([[150, 8], [W - 170, H - 8]])
    const g = sk({ nodes: nodes.map((d) => ({ ...d })), links: links.map((d) => ({ ...d })) })
    return { nodes: g.nodes, links: g.links, H, path: sankeyLinkHorizontal(), maxDepth: Math.max(...counts.keys()) }
  })

  function selectTid(tid: string, label: string, state: string | null) {
    selectMilestone({
      id: Number(tid.slice(2)), title: label, state, project: null,
      description: null, completed_at: null, waiting_on: null, has_embedding: true,
    } as TaskMilestone)
  }
  // node click → expand-in-place (or open detail at a leaf)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function onNode(n: any) {
    if (n.tid === rootId) return
    if (n.hasKids) { toggleExpand(n.tid); return }
    if (typeof n.tid === 'string' && n.tid.startsWith('n:')) selectTid(n.tid, n.name, n.state ?? null)
  }
  // link click → dive into the connection's metadata (relation + the target's
  // skill/procedure/audit substance), NOT expand. Nodes expand; links inspect.
  function onLink(targetTid: string, sourceTid: string) {
    selectConnection(sourceTid, targetTid)
  }

  // ── PIVOT (analytics) ──
  const get = (r: SankeyRow, d: Dim) => String((r as unknown as Record<string, unknown>)[d] ?? '')
  function toggleLayer(d: Dim) {
    const next = layers.includes(d) ? layers.filter((x) => x !== d) : [...layers, d]
    if (next.length < 2) return
    layers = ALL_DIMS.filter((x) => next.includes(x))
  }
  function toggleFilter(facet: Dim, value: string) {
    const cur = filters[facet] ?? []
    filters = { ...filters, [facet]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] }
  }
  const activeFilterCount = $derived(Object.values(filters).reduce((a, v) => a + v.length, 0))
  const facetValues = $derived.by(() => {
    const m: Record<string, Set<string>> = {}; for (const f of FILTER_FACETS) m[f] = new Set()
    for (const r of rows) for (const f of FILTER_FACETS) m[f].add(get(r, f))
    const out: Record<string, string[]> = {}; for (const f of FILTER_FACETS) out[f] = [...m[f]].sort(); return out
  })
  const filtered = $derived(rows.filter((r) => FILTER_FACETS.every((f) => !filters[f]?.length || filters[f].includes(get(r, f)))))
  function colorFor(layer: Dim, value: string): string {
    if (layer === 'state') return STATE_COLOR[value] || STATE_COLOR.other
    if (layer === 'visibility') return VIS_COLOR[value] || DIM_COLOR.visibility
    return DIM_COLOR[layer]
  }
  const layout = $derived.by(() => {
    if (pmode !== 'pivot' || !filtered.length || layers.length < 2) return null
    const ls = layers
    const idx = new Map<string, number>()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nodes: any[] = []
    const nid = (li: number, v: string) => li + '' + v
    const ensure = (li: number, v: string, layer: Dim) => {
      const id = nid(li, v); let i = idx.get(id)
      if (i === undefined) { i = nodes.length; idx.set(id, i); nodes.push({ name: v || '—', layer, li, color: colorFor(layer, v) }) }
      return i
    }
    const linkMap = new Map<string, number>()
    for (const r of filtered) {
      const vals = ls.map((l) => get(r, l))
      for (let i = 0; i < ls.length; i++) ensure(i, vals[i], ls[i])
      for (let i = 0; i < ls.length - 1; i++) { const k = nid(i, vals[i]) + '>' + nid(i + 1, vals[i + 1]); linkMap.set(k, (linkMap.get(k) || 0) + r.count) }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const links: any[] = [...linkMap].map(([k, v]) => { const [s, t] = k.split('>'); return { source: idx.get(s)!, target: idx.get(t)!, value: v } })
    const H = Math.max(420, Math.max(...ls.map((_, li) => nodes.filter((n) => n.li === li).length)) * 19)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sk = (sankey() as any).nodeWidth(12).nodePadding(8).nodeSort(null).extent([[150, 8], [W - 150, H - 8]])
    const g = sk({ nodes: nodes.map((d) => ({ ...d })), links: links.map((d) => ({ ...d })) })
    return { nodes: g.nodes, links: g.links, H, path: sankeyLinkHorizontal(), lastLi: ls.length - 1 }
  })
</script>

<div class="sankey">
  <div class="s-head">
    <span class="s-modes">
      <button class:on={pmode === 'flow'} onclick={() => pmode = 'flow'}>FLOW</button>
      <button class:on={pmode === 'pivot'} onclick={() => pmode = 'pivot'}>PIVOT</button>
    </span>
    {#if pmode === 'flow'}
      <span class="s-scope">{rootLabel}{#if rootId !== 'all'}<span class="s-scopekind"> · flow</span>{/if}</span>
      <span class="s-hint">click a node to reveal depth · click a flow to inspect its metadata</span>
    {:else}
      <span class="s-dims">layers:{#each ALL_DIMS as d (d)}<button class="s-dim" class:on={layers.includes(d)} onclick={() => toggleLayer(d)}>{d}</button>{/each}</span>
      <button class="s-dim s-filterbtn" class:on={showFilters || activeFilterCount > 0} onclick={() => showFilters = !showFilters}>⚲ filters{activeFilterCount ? ' (' + activeFilterCount + ')' : ''}</button>
    {/if}
  </div>
  {#if pmode === 'pivot' && showFilters}
    <div class="s-filterbar">
      {#each FILTER_FACETS as f (f)}<span class="s-fgroup"><span class="s-fkey">{f}</span>
        {#each facetValues[f] as v (v)}<button class="s-fval" class:off={filters[f]?.length && !filters[f].includes(v)} style:border-color={filters[f]?.includes(v) ? colorFor(f, v) : '#2a313c'} onclick={() => toggleFilter(f, v)}>{v}</button>{/each}
      </span>{/each}
    </div>
  {/if}
  <div class="s-scroll">
    {#if loading}<div class="s-status">loading flow…</div>
    {:else if error}<div class="s-status err">{error}</div>
    {:else if pmode === 'flow' && flow}
      <svg width={W} height={flow.H}>
        {#each flow.links as lk (lk.index)}
          <path class="s-flowlink" d={flow.path(lk)} fill="none" stroke={lk.tcolor} stroke-opacity="0.3" stroke-width={Math.max(1, lk.width)}
            onclick={() => onLink(lk.target.tid, lk.source.tid)} role="button" tabindex="-1" aria-label="inspect connection" />
        {/each}
        {#each flow.nodes as n (n.index)}
          <g class:drillable={n.hasKids && n.tid !== rootId} class:selectable={!n.hasKids && typeof n.tid === 'string' && n.tid.startsWith('n:')}
            onclick={() => onNode(n)} role="button" tabindex="-1">
            <rect x={n.x0} y={n.y0} width={n.x1 - n.x0} height={Math.max(1, n.y1 - n.y0)} rx="2" fill={n.color}><title>{n.name}</title></rect>
            {#if n.x0 < 80}<text x={n.x0 - 6} y={(n.y0 + n.y1) / 2 + 3} text-anchor="end" class="s-label s-rootlabel">{n.name.slice(0, 30)}</text>
            {:else}<text x={n.x1 + 6} y={(n.y0 + n.y1) / 2 + 3} text-anchor="start" class="s-label" class:s-state={n.kind === 'node'}>{n.hasKids ? (n.open ? '▾ ' : '▸ ') : ''}{n.name.slice(0, 44)}</text>{/if}
          </g>
        {/each}
      </svg>
    {:else if pmode === 'flow'}
      <div class="s-status">{treeState.error ? 'tree error: ' + treeState.error : (slug ? 'this track has no flow yet' : 'nothing to flow')}</div>
    {:else if pmode === 'pivot' && layout}
      <svg width={W} height={layout.H}>
        {#each layout.links as lk (lk.index)}<path d={layout.path(lk)} fill="none" stroke={lk.target.color} stroke-opacity="0.3" stroke-width={Math.max(1, lk.width)} />{/each}
        {#each layout.nodes as n (n.index)}<rect x={n.x0} y={n.y0} width={n.x1 - n.x0} height={Math.max(1, n.y1 - n.y0)} rx="2" fill={n.color}><title>{n.layer}: {n.name} ({n.value})</title></rect>
          {#if n.li === 0}<text x={n.x0 - 5} y={(n.y0 + n.y1) / 2 + 3} text-anchor="end" class="s-label">{n.name}</text>
          {:else}<text x={n.x1 + 5} y={(n.y0 + n.y1) / 2 + 3} text-anchor="start" class="s-label" class:s-state={n.li === layout.lastLi}>{n.name}</text>{/if}
        {/each}
      </svg>
    {:else}<div class="s-status">no rows match the filters</div>{/if}
  </div>
</div>

<style>
  .sankey { position: absolute; inset: 0; overflow: hidden; display: flex; flex-direction: column;
    font-family: var(--font-mono, ui-monospace, monospace); color: #e6edf3; background: #0d1117; }
  .s-head { padding: 8px 14px; font-size: 11px; color: #b9c4d0; border-bottom: 1px solid #2a313c; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .s-scope { letter-spacing: 0.04em; color: #e6edf3; font-weight: 600; }
  .s-scopekind { color: #6e7681; font-weight: 400; }
  .s-hint { color: #6e7681; font-size: 10px; margin-left: auto; }
  .s-modes { display: inline-flex; border: 1px solid #2a313c; border-radius: 4px; overflow: hidden; }
  .s-modes button { font: inherit; font-size: 10px; letter-spacing: .06em; padding: 3px 9px; background: transparent; border: none; border-left: 1px solid #2a313c; color: #6e7681; cursor: pointer; }
  .s-modes button:first-child { border-left: none; }
  .s-modes button.on { background: #15324a; color: #39d0d8; }
  .s-dims { display: inline-flex; align-items: center; gap: 4px; color: #6e7681; font-size: 10px; flex-wrap: wrap; }
  .s-dim { font: inherit; font-size: 10px; text-transform: uppercase; letter-spacing: .04em; padding: 3px 7px; background: transparent; border: 1px solid #2a313c; border-radius: 4px; color: #6e7681; cursor: pointer; }
  .s-dim:hover { color: #39d0d8; }
  .s-dim.on { background: #15324a; border-color: #39d0d8; color: #39d0d8; }
  .s-filterbtn { margin-left: auto; }
  .s-filterbar { padding: 7px 14px; border-bottom: 1px solid #2a313c; display: flex; flex-wrap: wrap; gap: 12px; background: #0f141b; }
  .s-fgroup { display: inline-flex; align-items: center; gap: 4px; flex-wrap: wrap; }
  .s-fkey { font-size: 9px; letter-spacing: .1em; text-transform: uppercase; color: #6e7681; margin-right: 2px; }
  .s-fval { font: inherit; font-size: 10px; padding: 2px 7px; background: #161b22; border: 1px solid #2a313c; border-radius: 10px; color: #c9d4e0; cursor: pointer; }
  .s-fval.off { opacity: 0.4; }
  .s-scroll { flex: 1; overflow: auto; padding: 6px 14px; }
  .s-status { color: #6e7681; font-size: 12px; padding: 16px; }
  .s-status.err { color: #f85149; }
  .s-label { fill: #8b949e; font-size: 9.5px; }
  .s-rootlabel { fill: #c9d4e0; }
  .s-state { fill: #c9d4e0; text-transform: capitalize; }
  .s-flowlink { cursor: pointer; transition: stroke-opacity .1s; }
  .s-flowlink:hover { stroke-opacity: 0.6 !important; }
  g.drillable { cursor: pointer; }
  g.drillable:hover rect { stroke: #39d0d8; stroke-width: 2; }
  g.drillable:hover .s-label { fill: #39d0d8; }
  g.selectable { cursor: pointer; }
  g.selectable:hover rect { stroke: #8957e5; stroke-width: 2; }
</style>
